import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteModule } from '@angular/material/autocomplete';
import { debounceTime, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { AwareHttpRequest } from '@appbolaget/aware-http';
import { Observable, Subject, of } from 'rxjs';
import { Model } from '@models';
import { IAwareCollection } from '@helpers/interfaces/collection.interface';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { Select } from '@ngxs/store';
import { UnitState } from 'app/state/unit.state';
import { Unit } from '@appbolaget/aware-model';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
    selector: 'aw-autocomplete-2',
    templateUrl: 'autocomplete-2.component.html',
    styleUrls: ['./autocomplete-2.scss'],
    standalone: true,
    imports: [
        MatFormFieldModule,
        NgIf,
        MatInputModule,
        FormsModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatProgressBarModule,
        NgFor,
        MatOptionModule,
        AsyncPipe,
    ],
})
export class AwareAutoCompleteComponent implements OnInit, OnDestroy {
    @Select(UnitState.activeUnit) activeUnit$: Observable<Unit>;

    _value: any = '';
    _request: AwareHttpRequest;
    _previousValue: any;

    formControl = new UntypedFormControl();
    options$: Observable<any[]>;
    focus$ = new Subject();
    refresher$ = new Subject<string | void>();

    ALIVE: boolean;
    IS_LOADING: boolean;
    HAS_FOCUSED: boolean;
    HAS_SELECTED: boolean;

    @ViewChild('textInput') textInput: ElementRef;
    @ViewChild('auto') autocomplete: MatAutocomplete;

    @Input() appearance: MatFormFieldAppearance = 'fill';
    @Input() excludes: Array<any>;
    @Input() placeholder = 'Sök';
    @Input() label: string;
    @Input() displayProperty = 'autocompleteLabel';

    @Input('value')
    set value(value) {
        this._value = value;

        if (value) {
            this._setValue(value);
        } else {
            this._clearInput();
        }
    }

    get value() {
        return this._value;
    }

    @Input() set request(request: AwareHttpRequest) {
        this._request = request;
    }

    get request() {
        return this._request;
    }

    @Output() onSelect = new EventEmitter();

    constructor(protected cdRef: ChangeDetectorRef) {}

    ngOnInit() {
        this.ALIVE = true;

        this.formControl.valueChanges
            .pipe(
                takeWhile(() => this.ALIVE),
                debounceTime(50),
                map((object) => this.displayFn(object)),
            )
            .subscribe((value) => {
                if (value && value === this._previousValue) {
                    return;
                }

                if (this.HAS_SELECTED) {
                    this.refresher$.next(null);
                    this.HAS_SELECTED = false;

                    return;
                }

                this.refresher$.next(value);
            });

        this.options$ = this.refresher$.pipe(
            takeWhile(() => this.ALIVE),
            debounceTime(500),
            tap(() => (this.IS_LOADING = true)),
            debounceTime(500),
            switchMap((query) => {
                if (!query || query?.length < 1) {
                    return of([]);
                }

                return this.request
                    .parameter('query', query)
                    .execute()
                    .pipe(map((collection: IAwareCollection<Model>) => collection.data));
            }),
            tap(() => {
                this.IS_LOADING = false;
            }),
        );
    }

    ngOnDestroy() {
        this.ALIVE = false;
    }

    clearValue() {
        this.formControl.setValue(null);
        this.formControl.markAsUntouched();

        setTimeout(() => {
            this.textInput.nativeElement.value = '';
        });
    }

    displayFn(obj: any): string {
        return obj && typeof obj === 'object' ? obj[this.displayProperty] : obj;
    }

    onFocus() {
        if (!this.HAS_FOCUSED) {
            this.refresher$.next();

            this.HAS_FOCUSED = true;
        }
    }

    optionSelected(option: MatAutocompleteSelectedEvent) {
        if (option.option.value) {
            this.HAS_SELECTED = true;
            this.onSelect.emit(option.option.value);
        }
    }

    private _setValue(value) {
        this.formControl.setValue(value[this.displayProperty]);
        this.formControl.markAsTouched();

        setTimeout(() => {
            this.textInput.nativeElement.value = value[this.displayProperty];
        });
    }

    private _clearInput() {
        this.formControl.setValue('');
        this.formControl.markAsUntouched();

        setTimeout(() => {
            this.textInput.nativeElement.value = '';
        }, 10);
    }
}
